import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el, en } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Action {
  type: string;
  label: string;
  status: 'pending' | 'accepted' | 'rejected';
  data: any;
}

interface Insight {
  id: string;
  source_type: 'transcript' | 'email' | 'proactive_alert';
  title: string;
  description: string;
  actions: Action[];
  status: 'pending' | 'accepted' | 'rejected';
  created_at: string;
}

const MODULE_COLORS: Record<string, string> = {
  risks: '#ef4444',
  issues: '#f59e0b',
  schedule: '#0ea5e9',
  stakeholders: '#8b5cf6',
  deliverables: '#10b981',
};

const AIAdvisor: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.aiAdvisor;
  const C = M.common;
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingAction, setProcessingAction] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('pending');
  const headers = { Authorization: `Bearer ${token}` };
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Run proactive checks silently, then fetch insights.
    // Errors aren't shown to the user (this runs on every page visit, unobtrusively), but they
    // ARE logged so a failed check is at least visible in devtools/support instead of vanishing.
    axios.post(`${API}/advisor/${project.id}/proactive-checks`, {}, { headers })
      .then(res => { if (res.data?.error) console.error('AI Advisor proactive checks reported an error (see backend logs)'); })
      .catch(err => console.error('AI Advisor proactive checks request failed:', err?.message || err))
      .finally(() => fetchInsights());
    pollingRef.current = setInterval(fetchInsights, 60000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, []);

  const fetchInsights = async () => {
    try {
      const res = await axios.get(`${API}/advisor/${project.id}/insights`, { headers });
      setInsights(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleAcceptAction = async (insightId: string, actionIndex: number) => {
    const key = `${insightId}-${actionIndex}`;
    setProcessingAction(key);
    try {
      await axios.post(
        `${API}/advisor/${project.id}/insights/${insightId}/accept/${actionIndex}`,
        { userId: user.id, userName: user.full_name },
        { headers }
      );
      await fetchInsights();
    } catch (err) { console.error(err); }
    finally { setProcessingAction(null); }
  };

  const handleRejectAction = async (insightId: string, actionIndex: number) => {
    const key = `${insightId}-${actionIndex}`;
    setProcessingAction(key);
    try {
      await axios.post(
        `${API}/advisor/${project.id}/insights/${insightId}/reject/${actionIndex}`,
        {},
        { headers }
      );
      await fetchInsights();
    } catch (err) { console.error(err); }
    finally { setProcessingAction(null); }
  };

  const handleDismiss = async (insightId: string) => {
    try {
      await axios.post(`${API}/advisor/${project.id}/insights/${insightId}/dismiss`, {}, { headers });
      await fetchInsights();
    } catch (err) { console.error(err); }
  };

  const handleAcceptAll = async (insight: Insight) => {
    for (let i = 0; i < insight.actions.length; i++) {
      if (insight.actions[i].status === 'pending') {
        await handleAcceptAction(insight.id, i);
      }
    }
  };

  const filteredInsights = insights.filter(i => filter === 'all' || i.status === filter);
  const pendingCount = insights.filter(i => i.status === 'pending').length;

  const formatTimeAgo = (d: string) => {
    const m = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
    if (m < 1) return L.timeJustNow;
    if (m < 60) return L.timeMinutes(m);
    const h = Math.floor(m / 60);
    if (h < 24) return L.timeHours(h);
    return new Date(d).toLocaleDateString(C.dateLocale);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
      {/* Header */}
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      {/* Breadcrumb */}
      <div style={{ padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky', top: 64, zIndex: 99 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: '500' }} onClick={onBack}>{project.name}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#374151', fontWeight: '500' }}>AI Advisor</span>
      </div>

      <div style={{ padding: '32px 48px', boxSizing: 'border-box' as const }}>
        {/* Title row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' }}>AI Advisor</h2>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>{L.subtitle}</p>
          </div>
          {pendingCount > 0 && (
            <div style={{ backgroundColor: '#ede9fe', border: '1px solid #c4b5fd', borderRadius: '10px', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '16px' }}>✦</span>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#4f46e5' }}>{L.pendingBadge(pendingCount)}</span>
            </div>
          )}
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px' }}>
          {[
            { key: 'pending', label: L.filterPending, count: insights.filter(i => i.status === 'pending').length },
            { key: 'all', label: L.filterAll, count: insights.length },
            { key: 'accepted', label: L.filterAccepted, count: insights.filter(i => i.status === 'accepted').length },
            { key: 'rejected', label: L.filterRejected, count: insights.filter(i => i.status === 'rejected').length },
          ].map(f => (
            <button key={f.key} onClick={() => setFilter(f.key as any)} style={{ padding: '6px 14px', borderRadius: '999px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', backgroundColor: filter === f.key ? '#4f46e5' : 'white', color: filter === f.key ? 'white' : '#6b7280', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {f.label} {f.count > 0 && <span style={{ marginLeft: '4px', opacity: 0.8 }}>({f.count})</span>}
            </button>
          ))}
        </div>

        {/* Insights */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: '#9ca3af' }}>{C.loading}</div>
        ) : filteredInsights.length === 0 ? (
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '60px', textAlign: 'center', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>✦</div>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px' }}>
              {filter === 'pending' ? L.emptyPendingTitle : L.emptyTitle}
            </h3>
            <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
              {filter === 'pending' ? L.emptyPendingDesc : L.emptyFilterDesc}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {filteredInsights.map(insight => (
              <div key={insight.id} style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', overflow: 'hidden', border: insight.status === 'pending' ? '1px solid #e0e7ff' : '1px solid #f3f4f6' }}>
                {/* Insight header */}
                <div style={{ padding: '16px 20px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: insight.source_type === 'transcript' ? '#dbeafe' : insight.source_type === 'proactive_alert' ? '#fef3c7' : '#fce7f3', color: insight.source_type === 'transcript' ? '#1e40af' : insight.source_type === 'proactive_alert' ? '#92400e' : '#9d174d', fontWeight: '500' }}>
                        {insight.source_type === 'transcript' ? '🎙️ Transcript' : insight.source_type === 'proactive_alert' ? '⚡ Αυτόματος Έλεγχος' : '📧 Email'}
                      </span>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>{insight.title}</span>
                      <span style={{ fontSize: '11px', color: '#9ca3af' }}>· {formatTimeAgo(insight.created_at)}</span>
                    </div>
                    <p style={{ fontSize: '14px', color: '#374151', margin: '0 0 8px', lineHeight: '1.5', fontWeight: '500' }}>{insight.description}</p>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {insight.actions?.map((a: any) => (
                        <span key={a.type} style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '999px', backgroundColor: '#f3f4f6', color: '#6b7280', fontWeight: '500', border: '1px solid #e5e7eb' }}>
                          {a.type?.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                  {canEdit && (
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                      {insight.status === 'pending' && (
                        <>
                          <button onClick={() => handleAcceptAll(insight)} style={{ backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' }}>{L.acceptAll}</button>
                          <button onClick={() => handleDismiss(insight.id)} style={{ backgroundColor: '#f3f4f6', color: '#6b7280', border: 'none', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}>{L.dismiss}</button>
                        </>
                      )}
                      {insight.status !== 'pending' && (
                        <span style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '999px', backgroundColor: insight.status === 'accepted' ? '#dcfce7' : '#f3f4f6', color: insight.status === 'accepted' ? '#166534' : '#6b7280', fontWeight: '500' }}>
                          {insight.status === 'accepted' ? L.statusAccepted : L.statusRejected}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{ padding: '14px 20px', backgroundColor: '#fafafa' }}>
                  <div style={{ fontSize: '11px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' }}>{L.suggestedActions}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {insight.actions?.map((action, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', backgroundColor: 'white', borderRadius: '8px', border: action.status === 'accepted' ? '1px solid #dcfce7' : action.status === 'rejected' ? '1px solid #f3f4f6' : '1px solid #e5e7eb', gap: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: '500', color: action.status === 'rejected' ? '#9ca3af' : '#374151', marginBottom: '2px', textDecoration: action.status === 'rejected' ? 'line-through' : 'none' }}>
                            {L.actionLabels[action.type] || action.type} — {action.label}
                          </div>
                          {action.data && action.status !== 'rejected' && (
                            <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
                              {action.data.title && <span>"{action.data.title}"</span>}
                              {action.data.probability && <span> · {L.probLabel}: {action.data.probability}</span>}
                              {action.data.impact && <span> · {L.impactLabel}: {action.data.impact}</span>}
                              {action.data.priority && <span> · {action.data.priority}</span>}
                            </div>
                          )}
                        </div>
                        {canEdit && (
                          <div style={{ flexShrink: 0 }}>
                            {action.status === 'pending' ? (
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                  onClick={() => handleAcceptAction(insight.id, i)}
                                  disabled={processingAction === `${insight.id}-${i}`}
                                  style={{ backgroundColor: '#dcfce7', color: '#166534', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                                >
                                  {processingAction === `${insight.id}-${i}` ? '...' : '✓'}
                                </button>
                                <button
                                  onClick={() => handleRejectAction(insight.id, i)}
                                  disabled={processingAction === `${insight.id}-${i}`}
                                  style={{ backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' }}
                                >✕</button>
                              </div>
                            ) : (
                              <span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '999px', backgroundColor: action.status === 'accepted' ? '#dcfce7' : '#f3f4f6', color: action.status === 'accepted' ? '#166534' : '#9ca3af', fontWeight: '500' }}>
                                {action.status === 'accepted' ? L.actionDone : L.actionRejected}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAdvisor;
